// @ts-nocheck
import { usePreferencesStore } from '../../../stores';
import { Box, Button, Card, Group, Stack, Switch, Text, Title } from '@/components/ui';
import { ScreenReaderOnly, SkipLink, useLiveRegion } from '../../../components/accessibility';
import { speak } from '../../../utils/accessibility';

export default function AccessibilityPage() {
  const {
    reducedMotion,
    highContrast,
    grayscaleMode,
    fontSize,
    lineSpacing,
    letterSpacing,
    uiSize,
    showIconLabels,
    enableTooltips,
    textToSpeech,
    strongFocus,
    setReducedMotion,
    setHighContrast,
    setGrayscaleMode,
    setFontSize,
    setLineSpacing,
    setLetterSpacing,
    setUiSize,
    setShowIconLabels,
    setEnableTooltips,
    setTextToSpeech,
    setStrongFocus,
  } = usePreferencesStore();

  const { announce } = useLiveRegion();

  const handleAnnounce = () => {
    const message = 'Live region announcement triggered';
    announce(message);
    if (textToSpeech) speak(message);
  };

  return (
    <Stack gap="md">
      <SkipLink label="Skip to demo content" href="#demo-content" />
      <Title order={2}>Accessibility Controls</Title>
      <Text c="dimmed" size="sm">
        These toggles are wired to preferences and CSS vars. Try them and see UI update live.
      </Text>

      <Card withBorder p="md" radius="md" id="demo-content">
        <Stack gap="sm">
          <Group gap="sm">
            <Switch
              label="Reduce motion"
              checked={reducedMotion}
              onChange={(e) => setReducedMotion(e.currentTarget.checked)}
            />
            <Switch
              label="High contrast"
              checked={highContrast}
              onChange={(e) => setHighContrast(e.currentTarget.checked)}
            />
            <Switch
              label="Grayscale"
              checked={grayscaleMode}
              onChange={(e) => setGrayscaleMode(e.currentTarget.checked)}
            />
            <Switch
              label="Strong focus"
              checked={strongFocus}
              onChange={(e) => setStrongFocus(e.currentTarget.checked)}
            />
          </Group>

          <Group gap="sm">
            <Switch
              label="Icon labels"
              checked={showIconLabels}
              onChange={(e) => setShowIconLabels(e.currentTarget.checked)}
            />
            <Switch
              label="Tooltips"
              checked={enableTooltips}
              onChange={(e) => setEnableTooltips(e.currentTarget.checked)}
            />
            <Switch
              label="Text to speech"
              checked={textToSpeech}
              onChange={(e) => setTextToSpeech(e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Card>

      <Card withBorder p="md" radius="md">
        <Text fw={600}>Typography & Spacing</Text>
        <Group gap="sm" mt="sm">
          <Button size="xs" variant={fontSize === 'xs' ? 'filled' : 'outline'} onClick={() => setFontSize('xs')}>
            XS text
          </Button>
          <Button size="xs" variant={fontSize === 'regular' ? 'filled' : 'outline'} onClick={() => setFontSize('regular')}>
            Regular text
          </Button>
          <Button size="xs" variant={fontSize === 'xl' ? 'filled' : 'outline'} onClick={() => setFontSize('xl')}>
            XL text
          </Button>
          <Button size="xs" variant={lineSpacing === 'tight' ? 'filled' : 'outline'} onClick={() => setLineSpacing('tight')}>
            Tight lines
          </Button>
          <Button size="xs" variant={lineSpacing === 'loose' ? 'filled' : 'outline'} onClick={() => setLineSpacing('loose')}>
            Loose lines
          </Button>
          <Button size="xs" variant={letterSpacing === 'wide' ? 'filled' : 'outline'} onClick={() => setLetterSpacing('wide')}>
            Wide letters
          </Button>
        </Group>
        <Group gap="sm" mt="sm">
          <Button size="xs" variant={uiSize === 'compact' ? 'filled' : 'outline'} onClick={() => setUiSize('compact')}>
            Compact UI
          </Button>
          <Button size="xs" variant={uiSize === 'regular' ? 'filled' : 'outline'} onClick={() => setUiSize('regular')}>
            Regular UI
          </Button>
          <Button size="xs" variant={uiSize === 'spacious' ? 'filled' : 'outline'} onClick={() => setUiSize('spacious')}>
            Spacious UI
          </Button>
        </Group>
      </Card>

      <Card withBorder p="md" radius="md">
        <Group justify="space-between" align="center" mb="sm">
          <Text fw={600}>Live region demo</Text>
          <Button size="xs" onClick={handleAnnounce}>
            Announce message
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          Trigger a polite aria-live announcement (and speech if enabled).
        </Text>
      </Card>
    </Stack>
  );
}
